import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'react-jss';
import { ReactNotifications } from 'react-notifications-component';
import { useCookie, useEffectOnce, useFullscreen } from 'react-use';
import jwtDecode from 'jwt-decode';
import { Route, Routes } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import Swal from 'sweetalert2';
import { TourProvider } from '@reactour/tour';
import ThemeContext from '../contexts/themeContext';

import Wrapper from '../layout/Wrapper/Wrapper';
import Portal from '../layout/Portal/Portal';
import { Toast, ToastContainer } from '../components/bootstrap/Toasts';
import useDarkMode from '../hooks/useDarkMode';
import COLORS from '../common/data/enumColors';
import { getOS, cookieNameGenerator, getRequester, getListRoles } from '../helpers/helpers';
import steps, { styles } from '../steps';
import Modal, { ModalBody } from '../components/bootstrap/Modal';
import Aside from '../layout/Aside/Aside';
import AuthModule from '../modules/AuthModule';
import UserModule from '../modules/UserModule';
import showNotification from '../components/extras/showNotification';
import { detectEmbedMode } from '../helpers/embedMode';
import { notifyEmbedBusy, notifyEmbedReady } from '../helpers/embedBridge';
import { ambilTujuanEmbed } from '../helpers/embedTujuan';

const accessToken = localStorage.getItem('accessToken') || null;
const appToken = localStorage.getItem('appToken') || null;
const IS_SSO = process.env.REACT_APP_SSO == 'true' || false;
const SSO_HOMEPAGE = process.env.REACT_APP_SSO_HOMEPAGE || null;

/**
 * Dibuka dari kartu inbox aplikasi mobile OneLNK (WebView), dibaca sekali dari
 * URL pertama. WebView memblokir host SSO, jadi di embed halaman tidak pernah
 * berpindah ke SSO_HOMEPAGE.
 */
const EMBED_AWAL = detectEmbedMode();

/**
 * Sesudah login dari cookie: kembali ke halaman kartu bila tercatat (embed,
 * lihat helpers/embedTujuan), selain itu muat ulang seperti sebelumnya.
 */
const muatSetelahLogin = () => {
	const tujuan = ambilTujuanEmbed();
	if (tujuan) {
		window.location.replace(tujuan);
		return;
	}
	window.location.reload();
};

const App = () => {
	const [cookie] = useCookie([cookieNameGenerator('ssocookies')]);
	const [cookieAcc] = useCookie([cookieNameGenerator('ssoaccount')]);
	const [cookiePhone] = useCookie([cookieNameGenerator('ssophonenumber')]);
	const [loadingSubmit, setLoadingSubmit] = useState(false);
	getOS();

	/**
	 * Dark Mode
	 */
	const { themeStatus, darkModeStatus } = useDarkMode();
	const theme = {
		theme: themeStatus,
		primary: COLORS.PRIMARY.code,
		secondary: COLORS.SECONDARY.code,
		success: COLORS.SUCCESS.code,
		info: COLORS.INFO.code,
		warning: COLORS.WARNING.code,
		danger: COLORS.DANGER.code,
		dark: COLORS.DARK.code,
		light: COLORS.LIGHT.code,
	};
	const testJSON = (text) => {
		if (typeof text !== 'string') {
			return false;
		}
		try {
			JSON.parse(text);
			return true;
		} catch (error) {
			return false;
		}
	};

	// role
	const { username, position_code } = getRequester();
	const { roles } = getListRoles();

	const [selectedRole, setSelectedRole] = useState([]);

	const onChange = (_position_code) => {
		let _selectRole = selectedRole;
		let is_change = false;
		const new_roles = roles.map((e) => {
			let is_selected = false;
			if (e?.position_code === _position_code) {
				_selectRole = e;
				is_selected = true;
				is_change = true;
			}
			return { ...e, is_selected };
		});

		if (is_change) {
			AuthModule.getMenu({ role: _selectRole?.role_name })
				.then(() => {
					localStorage.setItem('selectedRole', JSON.stringify(_selectRole));
					localStorage.setItem('roles', JSON.stringify(_selectRole?.role_name));
					localStorage.setItem('position', JSON.stringify(_selectRole));
					localStorage.setItem('list_roles', JSON.stringify(new_roles));
					window.location.href = '/home';
				})
				.catch(() => {
					Swal.fire({
						heightAuto: false,
						title: 'Warning!',
						text: 'Role not found',
						icon: 'info',
					});
				})
				.finally(() => {});
		}
	};

	useEffect(() => {
		if (username) {
			fetchDataRole();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const fetchDataRole = () => {
		UserModule.getPosition({ username }).then((res) => {
			const check = res.find((e) => e?.position_code === position_code);
			const checkAnother = res.filter((e) => e?.position_code !== position_code);

			if (check) {
				setSelectedRole(check);
			} else {
				onChange(checkAnother[0]?.position_code);
			}
		});
	};

	useEffectOnce(() => {
		const isSSOLogin = () => {
			try {
				if (!appToken) {
					AuthModule.generateToken().then(() => {
						return window.location.reload();
					});
				}
				const isEmailSSO = cookieAcc ? window.atob(cookieAcc) : null;
				const isHPSSO = cookiePhone ? window.atob(cookiePhone) : null;
				const decryptCookieSSO = cookie ? window.atob(cookie) : null;
				const roleLocal = localStorage.getItem('role');
				if (IS_SSO) {
					if (
						typeof roleLocal === 'string' &&
						['vendor vendor', 'security security'].includes(roleLocal)
					) {
						return isEmailSSO;
					}
					const tokenAccSSO = testJSON(decryptCookieSSO)
						? JSON.parse(decryptCookieSSO)
						: null;
					const decodeToken = tokenAccSSO ? jwtDecode(tokenAccSSO?.accessToken) : null;
					if (
						typeof roleLocal === 'string' &&
						!['vendor vendor', 'security security'].includes(roleLocal) &&
						!decryptCookieSSO &&
						accessToken
					) {
						if (EMBED_AWAL) {
							// Host SSO diblokir WebView: beri tahu, lepas penutup loading.
							Swal.fire({
								heightAuto: false,
								title: 'Sesi tidak ditemukan',
								text: 'Silakan login ke OneLNK lalu buka kartu ini lagi.',
								icon: 'info',
							});
							notifyEmbedReady({ isEmbed: true });
							return null;
						}
						AuthModule.goONELNKHomapage();
						window.location.href = SSO_HOMEPAGE;
						return SSO_HOMEPAGE;
					}
					//  isEmailSSo then get accessToken
					if (isEmailSSO && decodeToken && !accessToken) {
						setLoadingSubmit(true);
						notifyEmbedBusy({ isEmbed: EMBED_AWAL });
						const parseEmail = JSON.parse(isEmailSSO);
						const newPayload = {
							name: parseEmail?.name,
							email: parseEmail?.idTokenClaims?.preferred_username,
							account_id: parseEmail?.localAccountId,
							username: parseEmail?.localAccountId,
						};
						return AuthModule.loginSSO(newPayload)
							.then(async (res) => {
								if (res.accessToken) {
									const verifyToken = jwtDecode(res.accessToken);
									const default_role = res.role.find((ro) => ro.is_default);

									localStorage.setItem('accessToken', JSON.stringify(res));
									if (verifyToken.details.hris_org_tree.current_person) {
										// get menu
										await AuthModule.getMenu({ role: default_role.role_name })
											.then(() => {
												localStorage.setItem(
													'accessToken',
													JSON.stringify(res),
												);
												localStorage.setItem(
													'list_roles',
													JSON.stringify(res?.role),
												);
												localStorage.setItem(
													'roles',
													JSON.stringify(default_role?.role_name),
												);
												localStorage.setItem(
													'position',
													JSON.stringify({ ...default_role }),
												);
												localStorage.setItem(
													'photo',
													JSON.stringify({ ...res?.photo }),
												);
												muatSetelahLogin();
											})
											.catch((err) => {
												showNotification('Warning!', err, 'danger');
												notifyEmbedReady({ isEmbed: EMBED_AWAL });
											});
									}
								}
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
					// login by phone
					if (
						!isEmailSSO &&
						isHPSSO &&
						decodeToken &&
						!accessToken &&
						tokenAccSSO?.sso_key
					) {
						setLoadingSubmit(true);
						notifyEmbedBusy({ isEmbed: EMBED_AWAL });

						const parseEmail = JSON.parse(isHPSSO);
						return AuthModule.loginPhone(parseEmail)
							.then(async (res) => {
								if (res.accessToken) {
									const verifyToken = jwtDecode(res.accessToken);
									const default_role = [...res.role].find((e) => e?.is_default);

									if (verifyToken?.details?.hris_org_tree?.current_person) {
										await AuthModule.getMenu({ role: default_role?.role_name })
											.then(() => {
												localStorage.setItem(
													'accessToken',
													JSON.stringify(res),
												);
												localStorage.setItem(
													'list_roles',
													JSON.stringify(res?.role),
												);
												localStorage.setItem(
													'roles',
													JSON.stringify(default_role?.role_name),
												);
												localStorage.setItem(
													'position',
													JSON.stringify({ ...default_role }),
												);
												muatSetelahLogin();
											})
											.catch((err) => {
												showNotification('Warning!', err, 'danger');
												notifyEmbedReady({ isEmbed: EMBED_AWAL });
											});
									}
								}
							})
							.catch((err) => {
								Swal.fire({
									heightAuto: false,
									title: 'Warning!',
									text: err,
									icon: 'info',
								});
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
					//  !isEmailSSo && !isHPSSO then get accessToken
					if (
						!isEmailSSO &&
						!isHPSSO &&
						decodeToken &&
						!accessToken &&
						tokenAccSSO?.sso_key
					) {
						setLoadingSubmit(true);
						notifyEmbedBusy({ isEmbed: EMBED_AWAL });
						const accTokenParse = jwtDecode(tokenAccSSO.accessToken);
						return AuthModule.login(
							accTokenParse.username.toUpperCase(),
							accTokenParse.password,
						)
							.then(async (res) => {
								if (res.accessToken) {
									const verifyToken = jwtDecode(res.accessToken);
									const default_role = [...res.role].find((e) => e?.is_default);

									if (verifyToken?.details?.hris_org_tree?.current_person) {
										await AuthModule.getMenu({ role: default_role?.role_name })
											.then(() => {
												localStorage.setItem(
													'accessToken',
													JSON.stringify(res),
												);
												localStorage.setItem(
													'list_roles',
													JSON.stringify(res?.role),
												);
												localStorage.setItem(
													'roles',
													JSON.stringify(default_role?.role_name),
												);
												localStorage.setItem(
													'position',
													JSON.stringify({ ...default_role }),
												);
												localStorage.setItem(
													'photo',
													JSON.stringify({ ...res?.photo }),
												);
												muatSetelahLogin();
											})
											.catch((err) => {
												showNotification('Warning!', err, 'danger');
												notifyEmbedReady({ isEmbed: EMBED_AWAL });
											});
									}
								}
							})
							.finally(() => {
								setLoadingSubmit(false);
							});
					}
				}
				return isEmailSSO;
			} catch (err) {
				return Swal.fire({
					heightAuto: false,
					title: 'Warning!',
					text: err,
					icon: 'info',
				});
			}
		};
		isSSOLogin();
	});

	useEffect(() => {
		if (darkModeStatus) {
			document.documentElement.setAttribute('theme', 'dark');
		}
		return () => {
			document.documentElement.removeAttribute('theme');
		};
	}, [darkModeStatus]);

	/**
	 * Full Screen
	 */
	const { fullScreenStatus, setFullScreenStatus } = useContext(ThemeContext);
	const ref = useRef(null);
	useFullscreen(ref, fullScreenStatus, {
		onClose: () => setFullScreenStatus(false),
	});

	/**
	 * Modern Design
	 */
	useLayoutEffect(() => {
		if (process.env.REACT_APP_MODERN_DESGIN === 'true') {
			document.body.classList.add('modern-design');
		} else {
			document.body.classList.remove('modern-design');
		}
	});

	//	Add paths to the array that you don't want to be "Aside".
	const withOutAsidePages = ['login'];

	return (
		<ThemeProvider theme={theme}>
			<ToastProvider components={{ ToastContainer, Toast }}>
				<TourProvider
					steps={steps}
					styles={styles}
					showNavigation={false}
					showBadge={false}>
					<Modal
						isOpen={loadingSubmit}
						size='sm'
						isCentered
						setIsOpen={() => {}}
						isStaticBackdrop>
						<ModalBody
							style={{ backgroundColor: '#6c5dd3', color: 'white' }}
							className='text-center'>
							<button className='btn btn-primary' type='button' disabled>
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span
									className='spinner-grow spinner-grow-sm'
									role='status'
									aria-hidden='true'
								/>
								&nbsp;
								<span className='sr-only'>Loading...</span>
							</button>
						</ModalBody>
					</Modal>
					<div
						ref={ref}
						className='app'
						style={{
							backgroundColor: fullScreenStatus && 'var(--bs-body-bg)',
							zIndex: fullScreenStatus && 1,
							overflow: fullScreenStatus && 'scroll',
						}}>
						<Routes>
							{withOutAsidePages.map((path) => (
								<Route key={path} path={path} />
							))}
							<Route path='*' element={accessToken ? <Aside /> : null} />
						</Routes>
						<Wrapper />
					</div>
					<Portal id='portal-notification'>
						<ReactNotifications />
					</Portal>
				</TourProvider>
			</ToastProvider>
		</ThemeProvider>
	);
};

export default App;
